import React, { useContext, useMemo } from 'react';
import {
  Colors,
  ColorValues,
  Flex,
  IconChevronRight,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useSimilarGoods } from '../../../queries/useSimilarGoods';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { ScrollContext } from '../../../context/ScrollContext';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import {
  bridge,
  isApp,
  isNil,
  isNotNil,
  isWeb,
  useNavigator,
} from 'shared/src/utils';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { useLogClickSimilarGoodsMore } from '../hooks/useLogClickSimilarGoodsMore';
import { useLogDisplayRelatedGoods } from '../hooks/useLogDisplayRelatedGoods';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import { buildGoodsAnalyticsParams } from '../utils/buildGoodsAnalyticsParams';
import { buildFirstPageRendering } from '../utils/buildFirstPageRendering';
import { NetworkImageView } from 'native-views';

const PADDING_HORIZONTAL = 16;
const ITEM_SPACING = 8;

export const ITEM_SHOW_COUNT = 3;

const GoodsItemView: React.FC<{
  sno: number;
  title: string;
  price: string;
  image: string;
  discountPercent: string;
  width: number;
  onPress: () => void;
}> = props => {
  const height = useMemo(() => props.width * 1.2, [props.width]);

  return (
    <Flex
      position={'relative'}
      width={props.width}
      height={height}
      borderRadius={4}
      overflow={'hidden'}
      onPress={props.onPress}
    >
      {!isWeb ? (
        <NetworkImageView
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          source={{ uri: props.image, doAnimate: false }}
          width={props.width}
          height={height}
          style={{ flex: 1 }}
        />
      ) : (
        <RemoteImage uri={props.image} width={props.width} height={height} />
      )}
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        padding={{ horizontal: 8, vertical: 8 }}
      >
        <Flex position={'absolute'} left={0} right={0} bottom={0}>
          <Svg width={props.width} height={height * 0.4}>
            <Rect
              x={0}
              y={0}
              width={props.width}
              height={height * 0.4}
              fill="url(#paint0_linear_5546_5091)"
              fillOpacity="0.6"
            />
            <Defs>
              <LinearGradient
                id="paint0_linear_5546_5091"
                x1={height * 0.4 + 0.5}
                y1="0"
                x2={height * 0.4 + 0.5}
                y2={height * 0.4}
                gradientUnits="userSpaceOnUse"
              >
                <Stop stopOpacity="0" />
                <Stop offset="1" />
              </LinearGradient>
            </Defs>
          </Svg>
        </Flex>
        <Flex.Row gap={4}>
          <Typography type={'subtitle4'} rawColor={ColorValues.pink20}>
            {props.discountPercent}
          </Typography>
          <Typography type={'subtitle4'} color={'content_always_white'}>
            {props.price}
          </Typography>
        </Flex.Row>
        <Typography
          type={'body5'}
          color={'content_always_white'}
          numberOfLines={1}
          ellipsizeMode={'tail'}
        >
          {props.title}
        </Typography>
      </Flex>
    </Flex>
  );
};

export const RelatedGoodsView: React.FC<{ focused: boolean }> = ({
  focused = true,
}) => {
  const { scrollY } = useContext(ScrollContext);
  const goodsSno = useGoodsSno();

  const { data: similarGoods, isSuccess, isLoading } = useSimilarGoods(focused);
  const { width } = useScreenSize();

  const itemWidth = useMemo(() => {
    return (
      (width - PADDING_HORIZONTAL * 2 - ITEM_SPACING * 2) / ITEM_SHOW_COUNT
    );
  }, [width]);

  const targetList = useMemo(() => {
    if (!isSuccess) return [];
    if (!similarGoods) return [];
    if (similarGoods.goods.length < ITEM_SHOW_COUNT) return [];

    return similarGoods.goods.slice(0, ITEM_SHOW_COUNT);
  }, [isSuccess, similarGoods]);
  const { navigator } = useNavigator();
  const { openDeeplink } = useOpenDeeplink();

  useLogDisplayRelatedGoods({
    focused: focused,
    listId: similarGoods?.list_id ?? '',
    listParams: similarGoods?.list_params ?? '',
    targetList: targetList ?? [],
    isSuccess,
    isLoading,
  });
  const animStyle = useAnimatedStyle(() => {
    if (!scrollY || scrollY.value > 0) {
      return {};
    }
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-width, 0],
            [width * 0.5, 0],
            'clamp',
          ),
        },
      ],
    };
  });

  const logClickSimilarGoodsMore = useLogClickSimilarGoodsMore();

  if (isNil(targetList)) return null;

  // const targetList = relatedGoods.item_list[0];

  return (
    <Flex.Center position={'absolute'} top={0} bottom={0} left={0} right={0}>
      <Animated.View style={animStyle}>
        <Flex.Center gap={20}>
          {isLoading ? (
            <Flex.Row gap={ITEM_SPACING}>
              {Array.from({ length: ITEM_SHOW_COUNT }, (_, i) => i).map(
                item => (
                  <Flex
                    key={item}
                    width={itemWidth}
                    height={itemWidth * 1.2}
                    backgroundColorValue={ColorValues.gray55}
                    borderRadius={4}
                  ></Flex>
                ),
              )}
            </Flex.Row>
          ) : (
            <Flex.Row gap={ITEM_SPACING}>
              {targetList.map((item, index) => (
                <GoodsItemView
                  sno={item.sno}
                  key={item.sno.toString()}
                  title={item.name}
                  price={item.render.data.price}
                  image={item.image}
                  discountPercent={item.render.data.discount_rate}
                  width={itemWidth}
                  onPress={() => {
                    bridge.logAnalyticsEvent({
                      name: 'CLICK_GOODS',
                      ably_only_log: item.logging.ably_only_log,
                      analytics: {
                        SCREEN: 'GOODS_DETAIL',
                        LIST_ID: similarGoods?.list_id ?? '',
                        LIST_INDEX: index,
                        LIST_PARAMS: similarGoods?.list_params ?? '',
                        ...buildGoodsAnalyticsParams(item),
                        ...item.logging.analytics,
                      },
                    });

                    if (isNotNil(item.ad)) {
                      bridge.logAdEvent({
                        ad: item.ad,
                        action: AdAction.CLICK,
                      });
                    }
                    navigator.moveToGoods({
                      sno: item.sno,
                      firstPageRendering: buildFirstPageRendering(item),
                      ad: item.ad,
                    });
                  }}
                />
              ))}
            </Flex.Row>
          )}
          {isApp() && (
            <Flex.Row
              borderWidth={1}
              borderRadius={6}
              borderColor={'white'}
              padding={{ horizontal: 16, vertical: 8 }}
              gap={4}
              alignItems={'center'}
              onPress={() => {
                openDeeplink(
                  `ably://goods_list/?goods_sno=${goodsSno}&type=similar_goods&position=thumbnail&landing_from=thumbnail`,
                );
                logClickSimilarGoodsMore();
              }}
            >
              <Typography type={'subtitle4'} color={'content_always_white'}>
                관련 상품 더보기
              </Typography>
              <IconChevronRight fill={Colors.content_always_white} size={16} />
            </Flex.Row>
          )}
        </Flex.Center>
      </Animated.View>
    </Flex.Center>
  );
};
